// #region Imports
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject, DOCUMENT } from '@angular/core';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, finalize, first, map, switchMap, tap, timeout } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_URL_SEGMENT_PATTERN,
  LOCAL_STORAGE_LANGUAGE_KEY,
} from '../app.constants';
import { IndexedDBService } from './indexed-db.service';
import { UserService } from './user.service';
// #endregion Imports

// #region TypesAndInterfaces
/** Attesa prima di ricaricare la pagina dopo un cambio lingua esplicito. */
const LANGUAGE_CHANGE_RELOAD_DELAY_MS: number = 500;
// #endregion TypesAndInterfaces

// #region ComponentDecorator
/**
 * Punto unico di gestione della lingua: lettura/scrittura della preferenza,
 * cambio lingua da header/footer e risoluzione del prefisso lingua in URL (es. /EN/pagina).
 */
@Injectable({
  providedIn: 'root',
})
// #endregion ComponentDecorator
export class LanguageService {
  // #region State
  /** Lingua presa dall'URL e non ancora scritta sul profilo utente: serve il token */
  private pendingUserLanguage: string | null = null;

  /**
   * Tetto di attesa per le operazioni eseguite dall'APP_INITIALIZER.
   * Serve a garantire che un backend lento o un IndexedDB non disponibile degradino la feature
   * invece di lasciare l'utente davanti a una pagina bianca: il bootstrap deve sempre proseguire.
   */
  private readonly bootstrapTimeoutMs: number = 5000;
  // #endregion State

  // #region DependencyInjection
  private readonly document: Document = inject(DOCUMENT);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly indexedDBService: IndexedDBService = inject(IndexedDBService);

  // UserService viene risolto lazy: iniettarlo qui trascinerebbe AppService -> BootstrapService
  // dentro l'APP_INITIALIZER, anticipandone il costruttore che avvia getUser()/firstLoad().
  private readonly injector: Injector = inject(Injector);
  // #endregion DependencyInjection

  // #region PublicMethods

  /**
   * Ritorna la lingua salvata sul dispositivo, stringa vuota se non impostata.
   */
  getLanguage(): string {
    return localStorage.getItem(LOCAL_STORAGE_LANGUAGE_KEY) ?? '';
  }

  /**
   * Salva la lingua sul dispositivo e allinea l'attributo lang del documento.
   */
  setLanguage(language: string): void {
    localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, language);
    this.document.documentElement.lang = language;
  }

  /**
   * Garantisce che una lingua sia impostata, ricadendo sul default quando manca.
   *
   * @returns la lingua effettivamente in uso
   */
  ensureLanguage(): string {
    const currentLanguage: string = this.getLanguage();

    if (currentLanguage.length > 0) {
      this.document.documentElement.lang = currentLanguage;

      return currentLanguage;
    }

    this.setLanguage(DEFAULT_LANGUAGE);

    return DEFAULT_LANGUAGE;
  }

  /**
   * Risolve l'eventuale prefisso lingua presente nell'URL (es. /EN/pagina).
   * Se il prefisso è una lingua valida viene applicato e rimosso dall'URL, altrimenti
   * l'URL resta intatto (potrebbe essere uno slug SEO) e vale la lingua già salvata.
   *
   * Viene invocata da un APP_INITIALIZER, quindi prima che il Router faccia la sua prima
   * navigazione e prima che AppComponent legga window.location: l'URL arriva già ripulito
   * e non serve alcuna navigazione aggiuntiva. L'initializer si sottoscrive subito all'Observable
   * e attende che completi: emette una sola volta e non va mai in errore, così l'avvio prosegue sempre.
   */
  initLanguageFromUrl(): Observable<void> {
    // Qualunque errore qui non deve impedire l'avvio dell'applicazione: al massimo
    // si perde il prefisso lingua e si prosegue con la lingua già salvata.
    return defer((): Observable<void> => {
      const segment: string = this.getFirstUrlSegment();

      // Gate di forma: evita la chiamata HTTP su tutte le navigazioni senza prefisso lingua.
      if (segment.length === 0 || !LANGUAGE_URL_SEGMENT_PATTERN.test(segment)) {
        return of(undefined);
      }

      const language: string = segment.toUpperCase();

      return this.getSupportedLanguages().pipe(
        switchMap((supportedLanguages: string[]): Observable<void> => {
          // Non è una lingua nota: può essere uno slug SEO, quindi l'URL va lasciato intatto.
          if (!supportedLanguages.includes(language)) {
            return of(undefined);
          }

          this.stripLanguagePrefix();

          if (language === this.getLanguage()) {
            return of(undefined);
          }

          return from(this.applyLanguage(language, false)).pipe(
            // La scrittura sul profilo richiede il token, qui non ancora disponibile: rimandata a initPage().
            tap((): void => {
              this.pendingUserLanguage = language;
            })
          );
        })
      );
    }).pipe(
      catchError((error: unknown): Observable<void> => {
        console.error('initLanguageFromUrl', error);

        return of(undefined);
      }),
      // Su ogni percorso, anche dopo un errore, una lingua deve restare impostata.
      finalize((): void => {
        this.ensureLanguage();
      })
    );
  }

  /**
   * Cambio lingua esplicito dell'utente (bandiere di header e footer).
   * Ricarica la pagina per rigenerare tutti i contenuti nella nuova lingua.
   */
  changeLanguage(language: string): Observable<void> {
    return defer((): Promise<void> => this.applyLanguage(language, true)).pipe(
      switchMap((): Observable<void> => this.writeUserLanguage('changeLanguage', language)),
      tap((): void => {
        setTimeout((): void => {
          window.location.reload();
        }, LANGUAGE_CHANGE_RELOAD_DELAY_MS);
      })
    );
  }

  /**
   * Scrive sul profilo utente la lingua presa dall'URL.
   * Va invocata quando il token è disponibile, perché ChangeUserLanguage richiede autorizzazione.
   */
  flushPendingUserLanguage(): Observable<void> {
    return defer((): Observable<void> => {
      if (this.pendingUserLanguage === null) {
        return of(undefined);
      }

      const language: string = this.pendingUserLanguage;
      this.pendingUserLanguage = null;

      return this.writeUserLanguage('flushPendingUserLanguage', language);
    });
  }

  // #endregion PublicMethods

  // #region PrivateMethods

  /**
   * Recupera dal backend i codici delle lingue selezionabili sul sito.
   * L'endpoint è anonimo perché viene invocato prima che esista il token guest.
   *
   * @returns elenco dei codici in maiuscolo, array vuoto se la chiamata fallisce
   */
  private getSupportedLanguages(): Observable<string[]> {
    // Il segmento lingua nella route API è ininfluente per questa action: si usa il default,
    // come già fanno le altre chiamate di bootstrap (Auth/IT/GuestAuthorization).
    const url: string = `${environment.baseUrlApi}Content/${DEFAULT_LANGUAGE}/GetLanguages`;

    return this.http.get<string[]>(url).pipe(
      timeout(this.bootstrapTimeoutMs),
      map((response: string[]): string[] =>
        (response ?? [])
          .map((language: string): string => (language ?? '').trim().toUpperCase())
          .filter((language: string): boolean => language.length > 0)
      ),
      // Anche il timeout e una risposta illeggibile finiscono qui: l'avvio prosegue senza lingue note.
      catchError((error: unknown): Observable<string[]> => {
        console.error('getSupportedLanguages', error);

        return of<string[]>([]);
      })
    );
  }

  /**
   * Scrive sul profilo utente la lingua corrente. Un errore viene solo registrato: chi chiama
   * prosegue comunque, e l'Observable emette una volta sola.
   *
   * @param caller nome del metodo chiamante, per il log
   * @param language lingua scritta, per il log
   */
  private writeUserLanguage(caller: string, language: string): Observable<void> {
    return defer((): Observable<unknown> => this.injector.get(UserService).changeUserLanguage()).pipe(
      // first() prende la prima risposta e segnala come errore una chiusura senza risposta
      first(),
      map((): void => undefined),
      catchError((error: unknown): Observable<void> => {
        console.error(caller, language, error);

        return of(undefined);
      })
    );
  }

  /**
   * Nucleo condiviso del cambio lingua: preferenza, attributo lang e pulizia della cache.
   *
   * @param language codice lingua da applicare
   * @param shouldResetCache svuota la cache anche quando la lingua non cambia
   */
  private async applyLanguage(language: string, shouldResetCache: boolean): Promise<void> {
    const isLanguageChanged: boolean = language !== this.getLanguage();

    this.setLanguage(language);

    // La tabella Contents dell'IndexedDB è indicizzata per RequestName senza la lingua:
    // senza questa pulizia si servirebbero i contenuti cachati nella lingua precedente.
    if (isLanguageChanged || shouldResetCache) {
      await this.truncateContentCache();
    }
  }

  /**
   * Svuota la cache dei contenuti senza mai propagare errori né bloccare il chiamante.
   * truncateAllTables attende che l'IndexedDB sia pronto e respinge dopo 30 secondi: invocata
   * dall'APP_INITIALIZER significherebbe pagina bianca, quindi qui l'attesa è limitata e
   * il fallimento degrada a contenuti eventualmente ancora in cache.
   */
  private async truncateContentCache(): Promise<void> {
    try {
      await Promise.race([
        this.indexedDBService.truncateAllTables(),
        new Promise<void>((resolve: () => void): void => {
          setTimeout(resolve, this.bootstrapTimeoutMs);
        }),
      ]);
    } catch (error) {
      console.error('truncateContentCache', error);
    }
  }

  /**
   * Ritorna il primo segmento del path corrente, stringa vuota se il path è la root.
   */
  private getFirstUrlSegment(): string {
    return window.location.pathname.split('/').filter((segment: string): boolean => segment.length > 0)[0] ?? '';
  }

  /**
   * Rimuove il primo segmento dall'URL mantenendo query string e hash.
   *
   * Usa history.replaceState e non Location.replaceState di @angular/common perché gira prima
   * della prima navigazione del Router e non deve notificare i subscriber di Location.
   * replaceState (e non pushState) evita che il tasto Indietro riporti sull'URL con il prefisso.
   */
  private stripLanguagePrefix(): void {
    const segments: string[] = window.location.pathname
      .split('/')
      .filter((segment: string): boolean => segment.length > 0);

    segments.shift();

    const url: string = `/${segments.join('/')}${window.location.search}${window.location.hash}`;

    window.history.replaceState(window.history.state, '', url);
  }

  // #endregion PrivateMethods
}
